import React, { forwardRef, ChangeEvent, Ref } from "react";
import FormField from "../formField/FormField";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useBackgroundColor } from "../../../utils";
import { ChevronDown } from "../../icon";

export interface SelectProps {
  label: string;
  options: { value: string | number; label: string }[];
  defaultValue?: string | number;
  hideLabel?: boolean;
  required?: boolean;
  id: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  invalid?: boolean;
  valid?: boolean;
  errorMessage?: string;
}

interface StyledSelectProps {
  invalid?: boolean;
  valid?: boolean;
}

const StyledSelect = styled.select<StyledSelectProps>`
  ${({ theme: { space, colors, borderRadii }, invalid, valid }) => css`
    border: 2px solid
      ${valid ? colors.green : invalid ? colors.red : colors.darkBlue};
    ${useBackgroundColor("white")}
    padding: ${space.xxSmall} ${space.xSmall};
    border-radius: ${borderRadii.small};
    cursor: pointer;
    width: 100%;
    appearance: none;
  `};
`;

const StyledSelectWrapper = styled.div`
  ${({ theme: { space, colors, borderRadii } }) => css`
    position: relative;
    cursor: pointer;

    svg {
      position: absolute;
      right: ${space.xSmall};
      top: 50%;
      transform: translateY(-50%);
    }
  `};
`;

const StyledChevronDown = styled(ChevronDown)`
  ${({ theme: { colors } }) => css`
    pointer-events: none;
  `};
`;

export const Select = forwardRef(
  (
    {
      label,
      id,
      options,
      defaultValue,
      hideLabel,
      required,
      onChange,
      invalid,
      valid,
      errorMessage,
      ...rest
    }: SelectProps,
    ref: Ref<HTMLSelectElement>
  ) => {
    return (
      <FormField
        label={label}
        htmlFor={id}
        hideLabel={hideLabel}
        invalid={invalid}
        errorMesage={errorMessage || "This field is required"}
      >
        <StyledSelectWrapper>
          <StyledSelect
            id={id}
            defaultValue={defaultValue}
            required={required}
            onChange={onChange}
            ref={ref}
            invalid={invalid}
            valid={valid}
            aria-invalid={invalid} // Add aria-invalid attribute
            aria-describedby={`${id}-error`} // Reference to the error message
            {...rest}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </StyledSelect>
          <StyledChevronDown size="small" />
        </StyledSelectWrapper>
      </FormField>
    );
  }
);

export default Select;
